import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { openWhatsAppInquiry } from "@/lib/whatsapp";
import { logError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { name: "", company_name: "", email: "", phone: "", country: "", inquiry_details: "" };
const inputCls = "rounded-none border-border bg-background py-5 font-sans text-sm";

function InquiryFormFields({ form, setForm }) {
  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="inquiry-name" placeholder="Name *" value={form.name} onChange={update("name")} className={inputCls} required />
        <Input data-testid="inquiry-company" placeholder="Company Name" value={form.company_name} onChange={update("company_name")} className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="inquiry-email" placeholder="Email Address *" type="email" value={form.email} onChange={update("email")} className={inputCls} required />
        <Input data-testid="inquiry-phone" placeholder="Phone Number" value={form.phone} onChange={update("phone")} className={inputCls} />
      </div>
      <Input data-testid="inquiry-country" placeholder="Country" value={form.country} onChange={update("country")} className={inputCls} />
      <Textarea data-testid="inquiry-details" placeholder="Inquiry Details" value={form.inquiry_details} onChange={update("inquiry_details")} className="rounded-none border-border bg-background font-sans text-sm min-h-[120px]" />
    </>
  );
}

export const ContactSection = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in required fields");
      return;
    }
    setLoading(true);
    openWhatsAppInquiry(form, "Inquiry");
    try {
      await axios.post(`${API}/inquiries`, { ...form, inquiry_type: "general" });
      toast.success("Inquiry submitted! It has also been forwarded to our WhatsApp.");
      setForm(EMPTY_FORM);
    } catch (err) {
      logError("Failed to submit inquiry", err);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-xs font-sans uppercase tracking-[0.2em] text-[#C5A059] mb-4">Connect</p>
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-foreground mb-6">
              Let's Build Something Together
            </h2>
            <p className="text-base font-sans text-muted-foreground leading-relaxed mb-8">
              Reach out to explore collaborations, bulk orders, or custom developments.
            </p>
            <Link to="/contact">
              <Button
                data-testid="request-catalogue-btn"
                variant="outline"
                className="rounded-none px-8 py-5 text-xs uppercase tracking-[0.15em] font-sans border-foreground text-foreground hover:bg-foreground hover:text-white"
              >
                Request Catalogue <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="inquiry-form">
            <InquiryFormFields form={form} setForm={setForm} />
            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-inquiry-btn"
              className="bg-[#0F172A] text-white rounded-none px-8 py-6 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#0F172A]/90 w-full"
            >
              {loading ? "Submitting..." : "Submit Inquiry"}
              <ArrowRight size={14} className="ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
